import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, ArrowLeft, Paperclip, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Combobox } from "@/components/forms/Combobox";
import { Loading } from "@/components/common/Loading";
import { GroupAssociationFields } from "@/components/groups/GroupAssociationFields";
import { CoverUploader, uploadFile } from "@/components/forms/MediaUploader";
import { validateUpload } from "@/lib/uploadLimits";
import { RichTextEditor, FULL_TOOLBAR } from "@/components/forms/RichTextEditor";
import { MultiSelect } from "@/components/forms/MultiSelect";
import { AddressFields } from "@/components/forms/AddressFields";
import { useReferences } from "@/hooks/useReferences";
import { useAuth } from "@/context/AuthContext";
import { preferredTimezone, toRefValue } from "@/lib/timezones";

const opts = (l) => (l || []).map((x) => ({ value: x.value, label: x.label }));

// Rich-text counter — strips HTML and decodes common entities.
const PLAIN = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .trim().length;
const RefSelect = ({ list, value, onChange, placeholder, testId }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-64">{(list || []).map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
  </Select>
);

export default function EventEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { references } = useReferences();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [o, setO] = useState(null);
  const [saving, setSaving] = useState(false);
  // Groups + visibility (GRP-DEC-315/316). Seeded from the object's own
  // associations and audience scope, which only the server writes.
  const [groupIds, setGroupIds] = useState([]);
  const [visibility, setVisibility] = useState("public");
  const [businesses, setBusinesses] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // ONE place that seeds the Group controls, called only when the SERVER hands us the
  // object (load, or the Save reply). It used to be an effect on the whole document, so
  // every keystroke in an unrelated field re-seeded it and a Save reply without `groups`
  // emptied the selection outright (correction §§12/16).
  const hydrate = (doc) => {
    setO(doc);
    setGroupIds((doc.groups || [])
      .filter((l) => ["approved", "pending", "awaiting_owner"].includes(l.state))
      .map((l) => l.group_id));
    setVisibility(doc.scope === "group" ? "groups" : "public");
  };

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => hydrate(r.data)).catch(() => { toast.error("Not found"); navigate("/dashboard/events"); });
    api.get("/orgs/business/mine").then((r) => setBusinesses(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // One timezone drives the platform: a new event starts in the member's account timezone.
  useEffect(() => {
    if (!o || o.timezone || !(references.timezones || []).length) return;
    const value = toRefValue(references.timezones, preferredTimezone());
    if (value) setO((s) => ({ ...s, timezone: value }));
  }, [o, references.timezones]);

  if (!o) return <Loading />;
  const set = (k, v) => setO((s) => ({ ...s, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    { const chk = validateUpload(f, "event"); if (!chk.ok) { toast.error(chk.message); if (e.target) e.target.value = ""; return; } }
    if ((o.files || []).length >= 3) { toast.error("You can attach up to 3 files."); if (e.target) e.target.value = ""; return; }
    setUploading(true);
    try { const url = await uploadFile(f, "event"); set("files", [...(o.files || []), { url, name: f.name }]); toast.success("File attached"); }
    catch (_) { toast.error("Upload failed"); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const setFileName = (i, name) => set("files", (o.files || []).map((f, j) => (j === i ? { ...f, name } : f)));

  const save = async (extra = {}) => {
    if (PLAIN(o.description) > 5000) { toast.error("Description must be 5,000 characters or fewer."); return; }
    if (PLAIN(o.location_notes) > 300) { toast.error("Location notes must be 300 characters or fewer."); return; }
    if ((o.cta_label || "").trim().length > 25) { toast.error("Button label must be 25 characters or fewer."); return; }
    if (!(o.timezone || "").trim()) { toast.error("Timezone is required."); return; }
    if (o.organizer_type === "external" && !(o.host_name || "").trim()) { toast.error("Add who is hosting this event."); return; }
    setSaving(true);
    try {
      const body = { ...o, ...extra };
      ["id", "_id", "owner_id", "created_at", "status", "kind", "host", "is_owner", "location_geo", "groups", "group_results", "scope"].forEach((k) => delete body[k]);
      // Groups + audience are sent through their OWN authorized fields; `scope` is never
      // client-writable (GRP-DEC-316).
      body.group_ids = groupIds;
      body.visibility = visibility;
      const { data } = await api.put(`/events/${id}`, body);
      hydrate(data);
      // Per-Group outcome, so a Save never claims more than it achieved (§19).
      const failed = (data.group_results || []).filter((r) => r.error);
      if (failed.length) {
        toast.error(`Saved, but ${failed.length} group couldn't be added: ${failed[0].error}`);
      } else {
        toast.success("Saved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  const isVirtual = o.format === "virtual" || o.format === "hybrid";
  const isPhysical = o.format !== "virtual";

  return (
    <div className="mx-auto max-w-4xl">
      <Link to="/dashboard/events" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</Link>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">{o.title || "Event"}</h1>
        <div className="flex items-center gap-3">
          {o.published && o.slug && <Link to={`/event/${o.slug}`} target="_blank"><Button variant="secondary"><Eye className="mr-2 h-4 w-4" /> View public</Button></Link>}
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
            <Switch checked={!!o.published} onCheckedChange={(v) => save({ published: v })} data-testid="event-publish-toggle" />
            <span className="text-sm font-[540]">{o.published ? "Published" : "Hidden"}</span>
          </div>
          <Button onClick={() => save()} disabled={saving} data-testid="event-save-button-top">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <Card className="p-6">
          <h2 className="text-lg font-[640]">Cover</h2>
          <div className="mt-4"><CoverUploader value={o.cover_url} onChange={(u) => set("cover_url", u)} testId="event-cover" /></div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Details</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Title * <span className="font-[440] text-muted-foreground">({(o.title || "").length}/100)</span></Label>
              <Input maxLength={100} value={o.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="event-title" />
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Custom URL slug</Label><div className="flex items-center rounded-lg border border-input bg-card px-3"><span className="text-sm text-muted-foreground">startupotter.com/event/</span><input className="h-10 w-full bg-transparent px-1 text-sm outline-none" value={o.slug || ""} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} data-testid="event-slug" /></div></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Summary ({(o.summary || "").length}/100)</Label><Input maxLength={100} value={o.summary || ""} onChange={(e) => set("summary", e.target.value)} placeholder="One-line description" data-testid="event-summary" /></div>
            <div className="space-y-1.5"><Label>Category</Label><RefSelect list={references.event_categories} value={o.category} onChange={(v) => set("category", v)} placeholder="Select category" testId="event-category" /></div>
            <div className="space-y-1.5"><Label>Format</Label><RefSelect list={references.event_formats} value={o.format} onChange={(v) => set("format", v)} placeholder="Select format" testId="event-format" /></div>
            {isAdmin && (
              <div className="space-y-1.5"><Label>Listing type</Label>
                <Select value={o.source || "community"} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger data-testid="event-source"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="community">Community</SelectItem><SelectItem value="internal">Internal / Official</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5"><Label>Organizer</Label>
              <Select value={o.organizer_type || "personal"} onValueChange={(v) => { set("organizer_type", v); if (v !== "business") set("organizer_id", null); }}>
                <SelectTrigger data-testid="event-organizer-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="personal">Myself (personal)</SelectItem><SelectItem value="business">A business I run</SelectItem><SelectItem value="external">Someone else</SelectItem></SelectContent>
              </Select>
            </div>
            {o.organizer_type === "business" && (
              <div className="space-y-1.5 sm:col-span-2"><Label>Which business</Label><RefSelect list={businesses.map((b) => ({ value: b.id, label: b.display_name }))} value={o.organizer_id} onChange={(v) => set("organizer_id", v)} placeholder="Select a business" testId="event-organizer-business" /></div>
            )}
            {o.organizer_type === "external" && (
              <div className="space-y-1.5 sm:col-span-2"><Label>Hosted by *</Label><Input maxLength={150} value={o.host_name || ""} onChange={(e) => set("host_name", e.target.value)} placeholder="e.g. Colorado Technology Association" data-testid="event-host" /></div>
            )}
          </div>
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <Label>Description</Label>
              <span className={`text-xs tabular-nums ${PLAIN(o.description) > 5000 ? "font-[600] text-destructive" : "text-muted-foreground"}`} data-testid="event-description-count">{PLAIN(o.description).toLocaleString()}/5,000</span>
            </div>
            <RichTextEditor value={o.description} onChange={(v) => set("description", v)} toolbar={FULL_TOOLBAR} placeholder="What's this event about?…" testId="event-description" />
          </div>
          <div className="mt-4"><Label className="mb-2 block">Industries</Label><MultiSelect options={opts(references.industries)} value={o.industries || []} onChange={(v) => set("industries", v)} placeholder="Select industries" testId="event-industries" /></div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Date &amp; time</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Starts *</Label><Input type="datetime-local" value={o.start_at || ""} onChange={(e) => set("start_at", e.target.value)} data-testid="event-start" /></div>
            <div className="space-y-1.5"><Label>Ends</Label><Input type="datetime-local" value={o.end_at || ""} onChange={(e) => set("end_at", e.target.value)} data-testid="event-end" /></div>
            <div className="space-y-1.5"><Label>Timezone *</Label><Combobox options={(references.timezones || []).map((t) => ({ value: t.value, label: t.label }))} value={o.timezone || ""} onChange={(v) => set("timezone", v)} placeholder="Select timezone" searchPlaceholder="Search timezones…" emptyText="No timezone found." testId="event-timezone" /></div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Location &amp; registration</h2>
          {isPhysical && (
            <div className="mt-4 space-y-4">
              <div className="space-y-1.5"><Label>Venue name</Label><Input value={(o.location || {}).venue || ""} onChange={(e) => set("location", { ...(o.location || {}), venue: e.target.value })} placeholder="e.g. WeWork Embarcadero" data-testid="event-venue" /></div>
              <AddressFields value={o.location || {}} onChange={(l) => set("location", { ...(o.location || {}), ...l })} testIdPrefix="event-location" />
              <div className="space-y-1.5">
                {(() => {
                  // Strip HTML tags to count visible characters for the 300-cap.
                  const plain = PLAIN(o.location_notes);
                  return (
                    <Label>
                      Location notes <span className="font-[440] text-muted-foreground">(optional, {plain}/300)</span>
                    </Label>
                  );
                })()}
                <p className="text-xs text-muted-foreground">e.g. entrance codes, buzzer numbers, parking instructions. Bold, italic, underline and links only.</p>
                <RichTextEditor
                  value={o.location_notes || ""}
                  onChange={(v) => set("location_notes", v)}
                  toolbar={["bold", "italic", "underline", "link"]}
                  placeholder="Use entrance B, buzzer #1500, parking validated at the desk…"
                  testId="event-location-notes"
                />
              </div>
            </div>
          )}
          {isVirtual && (
            <div className="mt-4 space-y-1.5"><Label>Online link</Label><Input value={o.online_url || ""} onChange={(e) => set("online_url", e.target.value)} placeholder="https://zoom.us/…" data-testid="event-online-url" /></div>
          )}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Registration URL</Label><Input value={o.registration_url || ""} onChange={(e) => set("registration_url", e.target.value)} placeholder="https://… (Eventbrite, Luma, etc.)" data-testid="event-registration-url" /></div>
            <div className="space-y-1.5">
              <Label>Button label <span className="font-[440] text-muted-foreground">({(o.cta_label || "").length}/25)</span></Label>
              <Input
                maxLength={25}
                value={o.cta_label || ""}
                onChange={(e) => set("cta_label", e.target.value)}
                placeholder="Register, RSVP, Sign up…"
                data-testid="event-cta-label"
              />
            </div>
            <div className="space-y-1.5"><Label>Price</Label><Input value={o.price || ""} onChange={(e) => set("price", e.target.value)} placeholder="Free / $25" data-testid="event-price" /></div>
          </div>
          <div className="mt-4 space-y-1.5"><Label>Topics</Label><MultiSelect options={opts(references.resource_topics)} value={o.topics || []} onChange={(v) => set("topics", v)} placeholder="Select topics" testId="event-topics" /></div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Downloadable files</h2>
          <p className="mt-1 text-sm text-muted-foreground">Attach up to 3 files (agendas, decks, resources) — 25 MB max per file. Give each a clear name.</p>
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} />
          <div className="mt-3 space-y-2">
            {(o.files || []).map((f, i) => (
              <div key={f.url || `${f.name || "file"}-${i}`} className="flex items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2">
                <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                <Input value={f.name || ""} onChange={(e) => setFileName(i, e.target.value)} placeholder="File name / title" className="h-8 flex-1 border-0 bg-transparent px-1 focus-visible:ring-0" data-testid={`event-file-name-${i}`} />
                <button onClick={() => set("files", (o.files || []).filter((_, j) => j !== i))} className="shrink-0 text-muted-foreground hover:text-destructive" aria-label="Remove file" data-testid={`event-file-remove-${i}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
            {(o.files || []).length < 3 ? (
              <Button type="button" variant="secondary" onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading} data-testid="event-file-upload">
                {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Paperclip className="mr-2 h-4 w-4" />} Attach file
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">Maximum of 3 files reached.</p>
            )}
          </div>
        </Card>

        <Card className="p-6" data-testid="event-groups-card">
          <h2 className="mb-4 text-base font-[640]">Groups &amp; visibility</h2>
          <GroupAssociationFields
            contentType="event" testId="event-groups"
            value={groupIds} onChange={setGroupIds}
            visibility={visibility} onVisibilityChange={setVisibility}
            links={o.groups || []} published={Boolean(o.published)} />
        </Card>

        <Separator />
        <div className="flex justify-end"><Button size="lg" onClick={() => save()} disabled={saving} data-testid="event-save-button">{saving ? "Saving…" : "Save"}</Button></div>
      </div>
    </div>
  );
}
